import Vue from 'vue';
import uuidV4 from 'uuid/dist/esm-browser/v4';
import {consoleLog, consoleError} from 'xca-app-core/src/utils/console.js';

import FileChunker from './file-chunker.js';
import FileDigester from './file-digester.js';
import {enable as enableNoSleep, disable as disableNoSleep} from '../nosleep.js';
import {parseError, stringifyError} from '../utils.js';


const {store} = Vue;


export default class Peer {


  /**
   *
   * SENDER & RECEIVER
   *
   */


  constructor(serverConnection, peerId) {
    this.id = peerId;
    this.roomId = store.getters['share/getRoom']?.id;

    this._server = serverConnection;
    this._digesterIndex = 0;
    this._lastProgress = 0;
    this._digesters = [];
    this._chunkers = [];
    this._callbacks = {};
  }


  get peer() {
    const peer = store.getters['share/getPeer'](this.id);
    if (peer) {
      this._peer = peer; // Store last data.
    }
    return this._peer;
  }


  get room() {
    let room = store.getters['share/getRoom'];
    if (room?.id !== this.roomId) {
      room = null;
    }

    if (room) {
      this._room = room; // Store last data.
    }
    return this._room;
  }


  get roomConnection() {
    return store.getters['share/getRoomConnection'];
  }


  set progress(progress) {
    this._updatePeer({progress});
  }


  get progress() {
    return this.peer?.progress || 0;
  }


  set transfer(options) {
    const {type, files, description} = options ?? {};
    if (type && files) {
      this._updatePeer({transfer: {type, files, description}});
      this.noSleep = true;

    } else {
      this._updatePeer({transfer: null});
      this.noSleep = false;
    }
  }


  get transfer() {
    return this.peer?.transfer || null;
  }


  set noSleep(noSleep) {
    noSleep ? enableNoSleep(this.id) : disableNoSleep(this.id);
  }


  remove(opt_reason) {
    // Ensure this is only called once.
    if (this.removed) {
      return;
    }
    this.removed = true;


    let reason;
    if (opt_reason) {
      reason = opt_reason;
    } else {
      reason = this.roomConnection !== this.roomId ? 'room_left' : 'peer_left';
    }

    // Reject open/pending callbacks (also rejects `this.pending`)
    for (const callbackId in this._callbacks) {
      this._callbacks[callbackId](new Error(reason));
    }

    // Handle open file transfer
    if (this.transfer) {
      // Cancel open digesters
      this._digesters.forEach((digester) => digester.cancel());

      this._addNotification({
        type: 'peer-files-failed',
        store: true,
        data: {...this.transfer}
      });
    }

    // Handle open file request
    if (this.request) {
      const notification = store.getters['share/getNotifications'].find((n) => {
        return n.transferId === this.request.transferId;
      });
      if (notification) {
        this._removeNotification(notification.id);
      }
      this._addNotification({
        type: reason === 'room_left' ? 'peer-files-missed' : 'peer-files-expired',
        store: true,
        data: {...this.request}
      });
    }

    this.noSleep = false; // Disable noSleep for this peer.
  }


  _sendJSON(message, opt_callback) {
    let unwatch;

    if (typeof opt_callback === 'function') {
      const callbackId = uuidV4();
      unwatch = () => delete this._callbacks[callbackId];
      this._callbacks[callbackId] = opt_callback.bind(this);
      message.callbackId = callbackId;
    }
    this._consoleLog('send message:', message);
    this._send(JSON.stringify(message));

    return unwatch;
  }


  async _onMessage(message) {
    if (typeof message !== 'string') {
      return this._onChunkReceived(message); /* RECEIVER */
    }
    message = JSON.parse(message);
    this._consoleLog('received message:', message);

    if (message.type === 'callback') {
      const callback = this._callbacks[message.callbackId];
      if (typeof callback === 'function') {
        callback(parseError(message.error), message.response); // eslint-disable-line callback-return
        delete this._callbacks[message.callbackId];
      }
      return;
    }

    let response;
    let error = null;

    try {
      switch (message.type) {
        /**
         * SENDER
         */
        case 'partitionReceived':
          this._sendNextPartition();
          break;
        case 'progress':
          this.progress = message.progress;
          break;
        case 'transferComplete':
          this._onTransferCompleted();
          break;

        /**
         * RECEIVER
         */
        case 'request':
          response = await this._onFilesRequest(message);
          break;
        case 'cancel':
          this._onFilesCancel(message);
          break;
        case 'partition':
          this._sendJSON({type: 'partitionReceived', offset: message.offset});
          break;
        case 'text':
          this._onTextReceived(message.text);
          break;
      }
    } catch (err) {
      error = stringifyError(err);
    }

    if (message.callbackId) {
      this._sendJSON({type: 'callback', callbackId: message.callbackId, error, response});
    }
  }


  _addNotification(notification = {}) {
    notification.id = notification.id || uuidV4();

    // Add peer and room data.
    notification.data = notification.data || {};
    notification.data.peer = this.peer;
    notification.data.room = this.room;

    store.commit('share/addNotification', notification);
    return notification;
  }


  _removeNotification(notificationId) {
    store.commit('share/removeNotification', notificationId);
  }


  _updatePeer(data) {
    store.commit('share/updatePeer', {id: this.id, ...data});
    this.peer; // Update internal store.
  }


  _enableNoSleep() {
    enableNoSleep(this.id);
  }


  _disableNoSleep() {
    disableNoSleep(this.id);
  }


  /**
   *
   * SENDER
   *
   */


  get pending() {
    return this.peer?.pending;
  }


  set pending(pending) {
    this._updatePeer({pending});
    this.noSleep = pending;
  }


  sendFiles(files, description) {
    if (this.pending) {
      throw new Error('pending');
    }
    if (this.transfer) {
      throw new Error('transfer');
    }

    this.pending = true;

    let notification, unwatch;
    const transferId = uuidV4();
    const data = {
      files: files.map(({name, type, size}) => ({name, type, size})),
      description
    };

    const removePendingState = () => {
      this.pending = false; // Revert pending state
      if (notification) { // Remove pending notification
        this._removeNotification(notification.id);
      }
    };

    // Show pending notification
    notification = this._addNotification({
      type: 'peer-files-pending',
      silent: true, // Do not play sound
      persistent: true,
      data,
      reject: () => {
        // Cancel file request
        unwatch?.(); // Remove open pending callback
        removePendingState();
        this._sendJSON({type: 'cancel', transferId, ...data}); // Notify receiver
      }
    });

    // Send actual file request to receiver
    unwatch = this._sendJSON({type: 'request', transferId, ...data}, (error) => {
      // Callback from receiver
      removePendingState();

      if (error) {
        switch (error.message) {
          case 'declined':
            this._addNotification({
              type: 'peer-files-rejected',
              store: true,
              data
            });
            break;

          case 'peer_left':
            this._addNotification({
              type: 'peer-files-no-reply',
              store: true,
              data
            });
            break;

          case 'room_left':
            this._addNotification({
              type: 'peer-files-aborted',
              store: true,
              data
            });
            break;

          default:
            this._consoleError(error);
            break;
        }
        return;
      }

      // Store transferring files.
      this.transfer = {type: 'send', ...data};

      this._chunkers = files.map((file) => new FileChunker(
          file,
          (chunk) => this._send(chunk),
          (offset) => this._onPartitionEnd(offset)
      ));

      this._sendNextPartition();
    });
  }


  sendText(text) {
    const unescaped = btoa(unescape(encodeURIComponent(text)));
    this._sendJSON({type: 'text', text: unescaped});
  }


  _onPartitionEnd(offset) {
    this._sendJSON({type: 'partition', offset});
  }


  _sendNextPartition() {
    for (let i = 0; i < this._chunkers.length; ++i) {
      const chunker = this._chunkers[i];
      if (!chunker.isFileEnd) {
        return chunker.nextPartition();
      }
    }
  }


  _onTransferCompleted() {
    this.progress = 1;
    this._addNotification({
      type: 'peer-files-sent',
      store: true,
      data: {...this.transfer}
    });
    this.transfer = null; // Clear transferring files.
  }


  /**
   *
   * RECEIVER
   *
   */


  get request() {
    return this.peer?.request;
  }


  set request(request) {
    this._updatePeer({request});
    this.noSleep = request;
  }


  _onFilesRequest({files, description, transferId}) {
    return new Promise((resolve, reject) => {
      this.request = {files, description, transferId};

      let notification;

      const removeRequestState = () => {
        this.request = null;
        if (notification) {
          this._removeNotification(notification.id);
        }
      };

      notification = this._addNotification({
        type: 'peer-files-request',
        persistent: true,
        data: {files, description},
        transferId,
        resolve: () => {
          const transferredFiles = [];

          const fileTransfererd = (file) => {
            transferredFiles.push(file);
            if (transferredFiles.length === files.length) {
              this._onFilesReceived(transferredFiles, description);
            }
            this._digesterIndex = transferredFiles.length;
          };

          removeRequestState();
          this.transfer = {type: 'receive', files, description};

          this._digesterIndex = 0;
          this._lastProgress = 0;
          this._digesters = files.map((file) => new FileDigester(file, fileTransfererd));
          resolve();
        },
        reject: (error) => {
          removeRequestState();
          reject(error);
        }
      });
    });
  }


  _onFilesCancel({files, description, transferId}) {
    const notification = store.getters['share/getNotifications'].find((n) => {
      return n.transferId === transferId;
    });
    if (notification) {
      this._removeNotification(notification.id);
    }
    this.request = null;
    this._addNotification({
      type: 'peer-files-canceled',
      store: true,
      data: {files, description}
    });
  }


  _onChunkReceived(chunk) {
    const digesters = this._digesters;
    const digester = digesters[this._digesterIndex];
    digester.unchunk(chunk);

    const sum = (array) => array.reduce((a, b) => a + b, 0);
    const bytesReceived = sum(digesters.map((d) => d.bytesReceived));
    const size = sum(digesters.map((d) => d.meta.size));
    const progress = bytesReceived / size;

    this.progress = progress;

    // Occasionally notify SENDER about our progress.
    if (progress - this._lastProgress >= 0.01) {
      this._lastProgress = progress;
      this._sendJSON({type: 'progress', progress});
    }
  }


  _onFilesReceived(files, description) {
    this._sendJSON({type: 'transferComplete'});
    this.transfer = null; // Clear transferring files.

    this._addNotification({
      type: 'peer-files-received',
      store: true,
      data: {files, description}
    });
  }


  _onTextReceived(text) {
    this._addNotification({
      type: 'peer-text',
      store: true,
      data: {text: decodeURIComponent(escape(atob(text)))}
    });
  }


  _consoleLog(...args) {
    consoleLog(`[PEER](${this.id})`, ...args);
  }


  _consoleError(...args) {
    consoleError(`[PEER](${this.id})`, ...args);
  }
}
